import { ChevronRight } from 'lucide-react'
import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface TemplateVariableSyntaxGuideProps {
  className?: string
}

/**
 * Collapsible guide explaining the template variable syntax.
 * Uses native <details>/<summary> for accessible expand/collapse without state.
 */
export const TemplateVariableSyntaxGuide: FC<TemplateVariableSyntaxGuideProps> = ({
  className,
}): ReactNode => (
  <details className={cn('template-variable-syntax-guide group text-xs', className)}>
    <summary className="flex cursor-pointer items-center gap-1 text-muted-foreground hover:text-foreground select-none list-none">
      <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
      <span>Variable syntax reference</span>
    </summary>

    <div className="mt-2 space-y-2 text-muted-foreground">
      <SyntaxSection title="Basic syntax">
        <code>{'{variableName:type}'}</code>
      </SyntaxSection>

      <SyntaxSection title="Types">
        <SyntaxEntry label="text" description="Free text input" />
        <SyntaxEntry label="number" description="Numeric input" />
        <SyntaxEntry label="email" description="Email address" />
        <SyntaxEntry label="url" description="URL with validation" />
        <SyntaxEntry label="enum(A,B,C)" description="Dropdown selection" />
      </SyntaxSection>

      <SyntaxSection title="Constraints">
        <SyntaxEntry label="min(0)" description="Minimum value" />
        <SyntaxEntry label="max(100)" description="Maximum value" />
        <SyntaxEntry label="minLength(1)" description="Minimum text length" />
        <SyntaxEntry label="maxLength(255)" description="Maximum text length" />
        <SyntaxEntry label='regex("[a-z]+")' description="Regex pattern" />
      </SyntaxSection>

      <SyntaxSection title="Modifiers">
        <SyntaxEntry label='label("Field Name")' description="Display label" />
        <SyntaxEntry label='helper("Hint text")' description="Helper text" />
        <SyntaxEntry label='default("value")' description="Default value" />
      </SyntaxSection>

      <SyntaxSection title="Example">
        <code className="block rounded bg-muted px-2 py-1 text-[11px] leading-relaxed">
          {'{email:email:label("Work Email"):helper("Enter your work email")}'}
        </code>
      </SyntaxSection>
    </div>
  </details>
)

TemplateVariableSyntaxGuide.displayName = 'TemplateVariableSyntaxGuide'

interface SyntaxSectionProps {
  title: string
  children: ReactNode
}

const SyntaxSection: FC<SyntaxSectionProps> = ({ title, children }): ReactNode => (
  <div>
    <p className="mb-0.5 font-medium text-foreground">{title}</p>
    {children}
  </div>
)

SyntaxSection.displayName = 'SyntaxSection'

interface SyntaxEntryProps {
  label: string
  description: string
}

const SyntaxEntry: FC<SyntaxEntryProps> = ({ label, description }): ReactNode => (
  <div className="flex items-baseline gap-2">
    <code className="shrink-0 text-[11px]">{label}</code>
    <span className="text-[11px]">— {description}</span>
  </div>
)

SyntaxEntry.displayName = 'SyntaxEntry'
